import { lazy, Suspense, useMemo, useState } from 'react'
import { reloadOnce } from '../../lib/chunkReload'
import { fb } from '../../copyFeatBrowse'
import type { Card } from '../../lib/browse'
import { located } from '../../lib/explore'
import type { MapPoint } from '../PicksMap'
import { useOpenDetail } from './ListingCard'

// MapLibre is only downloaded when someone picks Map (it never weighs on Browse's first load)
const PicksMap = lazy(reloadOnce(() => import('../PicksMap')))

/**
 * R-06: the activities Browse is showing right now (every filter, words and ask included), as numbered pins. Tapping a pin
 * opens that activity's detail; closing it comes back to the map (the view lives in the URL as view=map).
 */
export function BrowseMap({ cards, more }: { cards: Card[]; more: boolean }) {
  const open = useOpenDetail()
  const [hovered, setHovered] = useState<string | null>(null)
  const [selected, setSelected] = useState<string | null>(null)
  const byId = useMemo(() => new Map(cards.map((c) => [c.item.id, c])), [cards])
  const points: MapPoint[] = useMemo(() => {
    const seen = new Set<string>()
    const out: MapPoint[] = []
    for (const c of cards) {
      if (!located(c.item) || seen.has(c.item.id)) continue
      seen.add(c.item.id)
      out.push({ id: c.item.id, n: out.length + 1, lat: c.item.lat as number, lng: c.item.lng as number, category: c.item.category, title: c.item.title })
    }
    return out
  }, [cards])
  const missing = cards.length - points.length
  const pick = (id: string) => {
    setSelected(id)
    const c = byId.get(id)
    if (c) open(c)
  }
  return (
    <div data-review-id="browse-map">
      <div className="relative h-[62vh] min-h-[360px] overflow-hidden rounded-[20px] ring-1 ring-line md:h-[560px] lg:h-[640px]" aria-label={fb('fb.map.label')} role="group">
        {points.length > 0 ? (
          <Suspense fallback={<div className="absolute inset-0 bg-sunk" />}>
            <PicksMap points={points} selected={selected} hovered={hovered} onSelect={pick} onHover={setHovered} className="absolute inset-0" />
          </Suspense>
        ) : (
          <div role="status" className="absolute inset-0 flex items-center justify-center bg-sunk p-6 text-center">
            <p className="t-meta max-w-[18rem] text-ink-2">{fb('fb.map.none')}</p>
          </div>
        )}
      </div>
      {(missing > 0 || more) && (
        <p className="t-caption mt-2 text-ink-2">
          {[missing > 0 ? (missing === 1 ? fb('fb.map.missing_one') : fb('fb.map.missing_other', { count: missing })) : null, more ? fb('fb.map.shown', { count: cards.length }) : null].filter(Boolean).join(' ')}
        </p>
      )}
    </div>
  )
}
